import { useEffect, useRef, useState } from "react";
import { applyCandidateStatus, sendProfessionalTestToCandidate } from "../../services/candidateApi";
import { getTests } from "../../services/testApi";
import { DATE_STATUS_VALUES } from "../../utils/candidateStatuses";
import { QUICK_ACTIONS, rejectStatusFor } from "../../utils/candidateFunnel";
import { STATUS_OPTIONS } from "./CandidatesToolbar";
import { errorText } from "./useCandidateActions";

/**
 * Mass actions and personal quick-action settings of «Кандидаты» (правка №4/№14, Чат 1).
 * [Claude] 2026-11-03. Mass actions go one candidate at a time and report what failed.
 */
export async function runForEach(items, task) {
    const failed = [];
    let done = 0;
    for (const item of items) {
        try {
            const result = await task(item);
            if (result === false) failed.push({ item, error: "не отправлено" });
            else done += 1;
        } catch (e) {
            failed.push({ item, error: errorText(e) });
        }
    }
    return { done, failed };
}

function summary(done, failed, verb) {
    if (!failed.length) return { text: `${verb}: ${done}`, tone: "success" };
    const names = failed.slice(0, 3).map((f) => f.item.full_name || `#${f.item.id}`).join(", ");
    return { text: `${verb}: ${done}, ошибок: ${failed.length} (${names}${failed.length > 3 ? "…" : ""}). ${failed[0].error}`, tone: done ? "warning" : "error" };
}

function BulkTestModal({ candidates, onClose, onDone }) {
    const [tests, setTests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [testId, setTestId] = useState("");
    const [message, setMessage] = useState("");
    const [sending, setSending] = useState(false);

    useEffect(() => {
        let alive = true;
        getTests()
            .then((all) => {
                if (!alive) return;
                setTests((Array.isArray(all) ? all : []).filter((t) => {
                    const type = String(t?.test_type || "").trim().toLowerCase();
                    return t?.publication_status !== "draft" && (type === "вопросно-ответная форма" || type === "questions");
                }));
            })
            .catch(() => alive && setTests([]))
            .finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, []);

    const send = async () => {
        const id = Number(testId);
        if (!Number.isFinite(id) || id <= 0) return;
        setSending(true);
        const result = await runForEach(candidates, async (candidate) => {
            const resp = await sendProfessionalTestToCandidate(candidate.id, id, message.trim() || null, {
                applicationId: candidate.application_id, vacancyId: candidate.vacancy_id || candidate.last_vacancy_id,
            });
            return !(resp?.delivery && resp.delivery !== "sent" && resp.delivery !== "ok");
        });
        setSending(false);
        onDone(summary(result.done, result.failed, "Тест отправлен"));
    };

    return <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onClick={() => !sending && onClose()}>
        <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="bulk-test-modal">
            <h3 className="text-lg font-bold text-slate-900">Отправить тест — {candidates.length} канд.</h3>
            <p className="mt-1 text-sm text-slate-500">Ссылка уйдёт каждому в hh.ru по его отклику. Статус сменится на «тест: отправлен».</p>
            <label className="mt-4 block text-xs font-semibold text-slate-500">Тест</label>
            <select value={testId} onChange={(e) => setTestId(e.target.value)} disabled={loading}
                className="mt-1 h-10 w-full rounded-lg border border-slate-200 px-2.5 text-sm outline-none focus:border-amber-400">
                <option value="">{loading ? "Загрузка…" : "Выберите тест"}</option>
                {tests.map((t) => <option key={t.id} value={t.id}>{t.name || t.title || `Тест #${t.id}`}</option>)}
            </select>
            <label className="mt-3 block text-xs font-semibold text-slate-500">Сообщение (необязательно)</label>
            <textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)}
                className="mt-1 w-full resize-none rounded-lg border border-slate-200 px-2.5 py-2 text-sm outline-none focus:border-amber-400" />
            <div className="mt-4 flex justify-end gap-2">
                <button type="button" disabled={sending} onClick={onClose} className="rounded-lg px-3 py-2 text-sm text-slate-600 hover:bg-slate-100">Отмена</button>
                <button type="button" disabled={sending || !testId} onClick={send}
                    className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50">
                    {sending ? "Отправляем…" : "Отправить"}
                </button>
            </div>
        </div>
    </div>;
}

export function CandidateBulkBar({ selected, pageCount, allOnPage, onSelectPage, onClear, onDone, showAlert }) {
    const [status, setStatus] = useState("");
    const [busy, setBusy] = useState(false);
    const [testOpen, setTestOpen] = useState(false);
    const count = selected.length;
    if (!count) return null;

    const finish = (res) => {
        showAlert(res.text, res.tone);
        onDone();
    };

    const applyStatus = async (getStatus, verb) => {
        let date = null;
        const sample = getStatus(selected[0]);
        if (DATE_STATUS_VALUES.has(sample)) {
            date = window.prompt("Дата статуса в формате ГГГГ-ММ-ДД", new Date().toISOString().slice(0, 10));
            if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return;
        }
        setBusy(true);
        const result = await runForEach(selected, (candidate) => applyCandidateStatus(candidate.id, getStatus(candidate), date));
        setBusy(false);
        finish(summary(result.done, result.failed, verb));
    };

    return <div className="sticky top-0 z-30 flex flex-wrap items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 shadow-sm" data-testid="candidate-bulk-bar">
        <span className="text-sm font-semibold text-slate-900">Выбрано: {count}</span>
        {!allOnPage ? <button type="button" onClick={onSelectPage} className="text-xs text-slate-600 underline-offset-2 hover:underline">Выбрать всех на странице ({pageCount})</button> : null}
        <span className="mx-1 h-5 w-px bg-amber-200" />
        <select value={status} onChange={(e) => setStatus(e.target.value)} disabled={busy} aria-label="Новый статус"
            className="h-8 max-w-[220px] rounded-lg border border-slate-200 bg-white px-2 text-sm">
            <option value="">Сменить статус…</option>
            {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.group}: {o.label}</option>)}
        </select>
        <button type="button" disabled={busy || !status} data-testid="bulk-apply-status"
            onClick={() => window.confirm(`Поставить статус «${status}» для ${count} канд.?`) && applyStatus(() => status, "Статус изменён")}
            className="h-8 rounded-lg bg-slate-900 px-3 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-40">Применить</button>
        <button type="button" disabled={busy} onClick={() => setTestOpen(true)} data-testid="bulk-send-test"
            className="h-8 rounded-lg border border-indigo-200 bg-white px-3 text-sm font-semibold text-indigo-700 hover:bg-indigo-50 disabled:opacity-40">Отправить тест</button>
        <button type="button" disabled={busy} data-testid="bulk-reject"
            onClick={() => window.confirm(`Отказать ${count} канд.? Статус подберётся по этапу: резюме, тест или собеседование.`) && applyStatus((c) => rejectStatusFor(c.status), "Отказано")}
            className="h-8 rounded-lg border border-rose-200 bg-white px-3 text-sm font-semibold text-rose-700 hover:bg-rose-50 disabled:opacity-40">Отказать</button>
        {busy ? <span className="text-xs text-slate-500">Выполняется…</span> : null}
        <button type="button" onClick={onClear} className="ml-auto text-xs text-slate-500 hover:text-slate-900">Снять выбор</button>
        {testOpen ? <BulkTestModal candidates={selected} onClose={() => setTestOpen(false)}
            onDone={(res) => { setTestOpen(false); finish(res); }} /> : null}
    </div>;
}

export function QuickActionsSettings({ value, onChange }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        if (!open) return undefined;
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);
    const keys = value || [];
    const toggle = (key) => onChange(keys.includes(key) ? keys.filter((k) => k !== key) : [...keys, key]);
    const move = (key, delta) => {
        const i = keys.indexOf(key);
        const j = i + delta;
        if (i < 0 || j < 0 || j >= keys.length) return;
        const next = [...keys];
        [next[i], next[j]] = [next[j], next[i]];
        onChange(next);
    };
    const byKey = Object.fromEntries(QUICK_ACTIONS.map((a) => [a.key, a]));
    return <div className="relative" ref={ref}>
        <button type="button" onClick={() => setOpen((v) => !v)} data-testid="quick-actions-settings"
            className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 hover:bg-slate-50">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
            Быстрые действия
        </button>
        {open ? <div className="absolute right-0 z-40 mt-1 w-72 rounded-xl border border-slate-200 bg-white p-3 shadow-xl">
            <p className="mb-2 text-xs text-slate-500">Кнопки в строке кандидата — только для вас. Порядок меняется стрелками.</p>
            <ul className="space-y-1">
                {keys.map((key, i) => <li key={key} className="flex items-center gap-2 rounded-lg bg-slate-50 px-2 py-1 text-sm">
                    <input type="checkbox" checked onChange={() => toggle(key)} className="h-4 w-4 accent-amber-500" aria-label={`Скрыть «${byKey[key]?.label}»`} />
                    <span className="flex-1 truncate">{byKey[key]?.label || key}</span>
                    <button type="button" disabled={i === 0} onClick={() => move(key, -1)} className="px-1 text-slate-400 hover:text-slate-800 disabled:opacity-30" aria-label="Выше">↑</button>
                    <button type="button" disabled={i === keys.length - 1} onClick={() => move(key, 1)} className="px-1 text-slate-400 hover:text-slate-800 disabled:opacity-30" aria-label="Ниже">↓</button>
                </li>)}
                {QUICK_ACTIONS.filter((a) => !keys.includes(a.key)).map((a) => <li key={a.key} className="flex items-center gap-2 px-2 py-1 text-sm text-slate-500">
                    <input type="checkbox" checked={false} onChange={() => toggle(a.key)} className="h-4 w-4 accent-amber-500" aria-label={`Показать «${a.label}»`} />
                    <span className="flex-1 truncate">{a.label}</span>
                </li>)}
            </ul>
        </div> : null}
    </div>;
}
